import React, { useState } from 'react';
import { motion } from 'motion/react';
import { 
  Users, 
  Scissors, 
  Calendar, 
  Settings, 
  LogOut, 
  ShieldCheck, 
  TrendingUp, 
  Clock, 
  Search,
  Filter,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Trash2,
  ChevronRight
} from 'lucide-react';
import { StatusBadge } from '../../ui/StatusBadge';
import { cn } from '../../../lib/utils';
import { UserProfile } from '../../../types';
import { dbService } from '../../../services/dbService';

interface AdminDashboardProps {
  user: UserProfile;
  stats: {
    totalUsers: number;
    totalBarbers: number;
    totalBookings: number;
    totalRevenue: number;
  };
  users: UserProfile[];
  registrations: any[];
  bookings: any[];
  onUpdateUserRole: (userId: string, role: string) => void;
  onApproveBarber: (reg: any) => void;
  onRejectBarber: (id: string) => void;
  onToggleVerify: (uid: string, verified: boolean) => void;
  onClearUsers: () => void;
  onDeleteUser: (uid: string) => void;
  onDeleteBooking: (id: string) => void;
  onLogOut: () => void;
  onHome: () => void;
}

export const AdminDashboard = ({ 
  user, 
  stats, 
  users, 
  registrations,
  bookings, 
  onUpdateUserRole, 
  onApproveBarber,
  onRejectBarber,
  onToggleVerify,
  onClearUsers,
  onDeleteUser,
  onDeleteBooking,
  onLogOut, 
  onHome 
}: AdminDashboardProps) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'users' | 'bookings' | 'settings' | 'registrations'>('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const adminTabs = [
    { id: 'overview', label: 'Dashboard', icon: TrendingUp },
    { id: 'users', label: 'User Management', icon: Users },
    { id: 'registrations', label: 'Barber Requests', icon: Clock, badge: registrations.length },
    { id: 'bookings', label: 'All Transactions', icon: Calendar },
    { id: 'settings', label: 'Platform Config', icon: Settings },
  ];

  const filteredUsers = users.filter(u => 
    u.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
    u.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-[#FDFDFD] font-sans selection:bg-emerald-500 selection:text-white">
      {/* Mobile Sticky Header */}
      <div className="md:hidden sticky top-0 z-[60] bg-slate-900 text-white p-6 border-b border-slate-800 flex items-center justify-between shadow-xl">
        <div className="flex items-center gap-3" onClick={onHome}>
          <div className="w-10 h-10 bg-white text-slate-900 rounded-xl flex items-center justify-center shadow-[3px_3px_0px_0px_rgba(16,185,129,1)]">
            <ShieldCheck className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-lg font-black tracking-tighter italic uppercase leading-none">Cukur<span className="text-emerald-400">Je</span></h1>
          </div>
        </div>
        <button 
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="p-3 bg-emerald-500 text-slate-900 rounded-xl shadow-[4px_4px_0px_0px_rgba(255,255,255,1)] active:translate-y-1 active:shadow-none transition-all"
        >
          {isMenuOpen ? <CheckCircle2 className="w-5 h-5" /> : <Filter className="w-5 h-5 -rotate-90" />}
        </button>
      </div>

      {/* Sidebar - Brutalist Edge */}
      <aside className={cn(
        "w-full md:w-80 bg-slate-900 text-white flex flex-col fixed md:sticky top-0 h-screen z-50 border-r border-slate-800 transition-transform duration-500 md:translate-x-0 overflow-y-auto",
        isMenuOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
      )}>
        <div className="p-10">
          <div className="hidden md:flex items-center gap-4 mb-12 group cursor-pointer" onClick={onHome}>
            <div className="w-12 h-12 bg-white text-slate-900 rounded-2xl flex items-center justify-center shadow-[4px_4px_0px_0px_rgba(16,185,129,1)] transition-transform group-hover:-translate-y-1 active:translate-y-0">
              <ShieldCheck className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-2xl font-black tracking-tighter leading-none italic uppercase group-hover:text-emerald-400 transition-colors">Cukur<span className="text-emerald-400">Je</span></h1>
              <p className="text-[9px] text-slate-500 font-black uppercase tracking-[0.3em] mt-1.5 ml-0.5 group-hover:text-slate-400 transition-colors">Beranda Admin</p>
            </div>
          </div>

          <nav className="space-y-2">
            {adminTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id as any);
                  setIsMenuOpen(false);
                }}
                className={cn(
                  "w-full flex items-center gap-4 px-6 py-5 rounded-2xl text-[10px] font-black uppercase tracking-[0.15em] transition-all duration-300 group",
                  activeTab === tab.id 
                    ? "bg-emerald-500 text-slate-900 shadow-[6px_6px_0px_0px_#0F172A] -translate-x-1" 
                    : "text-slate-400 hover:text-white hover:bg-white/5"
                )}
              >
                <tab.icon className={cn("w-4 h-4", activeTab === tab.id ? "animate-pulse" : "group-hover:rotate-12 transition-transform")} />
                {tab.label}
                {tab.badge && tab.badge > 0 ? (
                  <span className="ml-auto w-6 h-6 bg-red-500 text-white text-[10px] font-black rounded-lg flex items-center justify-center shadow-lg animate-bounce">
                    {tab.badge}
                  </span>
                ) : null}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-10 mt-auto bg-slate-950/50 border-t border-white/5 space-y-8">
          <div className="flex items-center gap-5">
            <div className="relative">
              <img src={user.avatar} className="w-12 h-12 rounded-2xl object-cover ring-2 ring-emerald-500/30 grayscale hover:grayscale-0 transition-all cursor-pointer" alt="" />
              <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-emerald-500 border-2 border-slate-900 rounded-full" />
            </div>
            <div>
              <p className="text-xs font-black text-white truncate tracking-tight">{user.name}</p>
              <p className="text-[9px] text-emerald-500 font-black uppercase tracking-widest leading-none mt-1.5">Root Administrator</p>
            </div>
          </div>
          <button 
            onClick={onLogOut}
            className="w-full flex items-center justify-center gap-3 py-4 bg-red-500/10 hover:bg-red-500 text-red-500 hover:text-white transition-all rounded-2xl text-[10px] font-black uppercase tracking-widest border border-red-500/20 group"
          >
            <LogOut className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            Kill Session
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 p-6 md:p-16 overflow-y-auto">
        <header className="mb-16 flex flex-col lg:flex-row lg:items-end justify-between gap-10">
          <div className="space-y-4">
            <div className="flex items-center gap-3 text-emerald-600">
              <div className="h-[2px] w-8 bg-emerald-500" />
              <span className="text-[10px] font-black uppercase tracking-[0.4em]">System Status: Online</span>
            </div>
            <h2 className="text-6xl md:text-7xl font-black text-slate-900 tracking-tighter leading-[0.85] uppercase">
              {adminTabs.find(t => t.id === activeTab)?.label}
            </h2>
          </div>
          <div className="flex flex-col sm:flex-row gap-4">
            <button 
              onClick={onHome}
              className="flex items-center justify-center gap-4 px-10 py-5 bg-white text-slate-900 border-2 border-slate-900 rounded-2xl text-[11px] font-black uppercase tracking-widest shadow-[6px_6px_0px_0px_rgba(15,23,42,1)] hover:translate-x-1 hover:translate-y-1 hover:shadow-none transition-all active:scale-95"
            >
              Public View
            </button>
          </div>
        </header>

        {activeTab === 'overview' && (
          <div className="space-y-20">
            {/* Massive Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
              {[
                { label: 'Total Base Users', value: stats.totalUsers, icon: Users, color: 'emerald', trend: '+12%' },
                { label: 'Verified Kapsters', value: stats.totalBarbers, icon: Scissors, color: 'slate', trend: '+5' },
                { label: 'Cumulative Orders', value: stats.totalBookings, icon: Calendar, color: 'emerald', trend: 'Active' },
                { label: 'Platform Revenue', value: stats.totalRevenue.toLocaleString(), icon: TrendingUp, color: 'slate', prefix: 'Rp ', trend: '↑' },
              ].map((stat, i) => (
                <motion.div 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  key={stat.label}
                  className={cn(
                    "p-10 border-2 flex flex-col justify-between min-h-[220px] transition-all",
                    stat.color === 'emerald' 
                      ? "bg-emerald-500 border-slate-900 shadow-[8px_8px_0px_0px_#0F172A]" 
                      : "bg-white border-slate-900 shadow-[8px_8px_0px_0px_#10B981]"
                  )}
                >
                  <div className="flex justify-between items-start">
                    <div className={cn(
                      "w-12 h-12 rounded-xl border-2 border-slate-900 flex items-center justify-center",
                      stat.color === 'emerald' ? "bg-white text-slate-900" : "bg-emerald-500 text-white"
                    )}>
                      <stat.icon className="w-5 h-5 shadow-sm" />
                    </div>
                    <span className={cn(
                      "text-[10px] font-black uppercase tracking-widest border px-2 py-1",
                      stat.color === 'emerald' ? "border-slate-900/20 text-slate-900/60" : "border-emerald-500/20 text-emerald-600"
                    )}>{stat.trend}</span>
                  </div>
                  <div>
                    <p className={cn(
                      "text-[10px] font-black uppercase tracking-widest mb-1",
                      stat.color === 'emerald' ? "text-slate-900/60" : "text-slate-400"
                    )}>{stat.label}</p>
                    <h3 className={cn(
                      "text-4xl font-black tracking-tighter tabular-nums",
                      stat.color === 'emerald' ? "text-slate-900" : "text-slate-900"
                    )}>
                      {stat.prefix}{stat.value}
                    </h3>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Transactions Section - Visible Grids */}
            <div className="space-y-8">
              <div className="flex items-center justify-between border-b-4 border-slate-900 pb-6">
                <div className="flex items-center gap-4">
                  <div className="w-4 h-4 bg-emerald-500 border-2 border-slate-900 rotate-45" />
                  <h3 className="text-3xl font-black text-slate-900 uppercase tracking-tighter italic">Live Feed</h3>
                </div>
                <button className="text-[11px] font-black text-emerald-600 hover:text-slate-900 uppercase tracking-[0.2em] transition-all flex items-center gap-2">
                  History Log <ChevronRight className="w-4 h-4" />
                </button>
              </div>
              
              <div className="grid grid-cols-1 gap-[1px] bg-slate-200 border-2 border-slate-900 overflow-hidden shadow-[12px_12px_0px_0px_rgba(241,245,249,1)]">
                {bookings.slice(0, 8).map((b) => (
                  <div key={b.id} className="bg-white p-8 md:p-10 hover:bg-slate-50 transition-all flex flex-col md:flex-row md:items-center justify-between gap-8 group">
                    <div className="flex items-center gap-8">
                      <div className="hidden sm:flex flex-col items-center justify-center w-16 h-16 border-2 border-slate-100 italic font-black text-slate-200 uppercase text-[10px] bg-slate-50 group-hover:bg-emerald-500 group-hover:text-white group-hover:border-slate-900 transition-all">
                        {b.date.split(' ')[0].slice(0,3)}
                      </div>
                      <div>
                        <div className="flex items-center gap-3 mb-1">
                          <p className="font-black text-xl text-slate-900 uppercase tracking-tight">{b.customerName}</p>
                          <span className="text-[9px] font-black bg-slate-900 text-white px-2 py-0.5 rounded-sm">#{b.id.slice(-6).toUpperCase()}</span>
                        </div>
                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-[0.1em]">Layanan: {b.serviceName} • {b.time}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-8 justify-between md:justify-end">
                      <div className="text-right hidden sm:block">
                        <p className="text-[9px] text-slate-400 font-black uppercase tracking-widest mb-1">Kapster</p>
                        <p className="text-xs font-black text-slate-900">{b.barberName}</p>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <div className="flex items-center gap-4">
                          <p className="text-lg font-black text-slate-900 tabular-nums leading-none">Rp {b.totalPrice.toLocaleString()}</p>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              onDeleteBooking(b.id);
                            }}
                            className="p-3 text-red-400 hover:text-white transition-all rounded-xl hover:bg-red-500 border border-transparent hover:border-red-600 shadow-sm"
                            title="Delete Transaction"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <StatusBadge status={b.status} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Improved Barber Requests */}
        {activeTab === 'registrations' && (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
            {registrations.length === 0 ? (
              <div className="col-span-full py-40 text-center bg-white border-4 border-dashed border-slate-100 rounded-[3rem]">
                <Clock className="w-20 h-20 text-slate-100 mx-auto mb-8 animate-pulse" />
                <p className="text-2xl font-black text-slate-300 uppercase tracking-tighter italic">Queue is Empty</p>
              </div>
            ) : (
              registrations.map((reg) => (
                <motion.div 
                  layout
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  key={reg.id} 
                  className="bg-white border-2 border-slate-900 shadow-[10px_10px_0px_0px_#10B981] p-10 flex flex-col gap-10 hover:-translate-y-1 transition-transform"
                >
                  <div className="flex items-start justify-between gap-6">
                    <div className="flex items-center gap-6">
                      <div className="w-20 h-20 bg-slate-900 rounded-3xl flex items-center justify-center text-emerald-400 font-black text-3xl shadow-xl">
                        {reg.name.charAt(0)}
                      </div>
                      <div>
                        <h3 className="text-3xl font-black text-slate-900 tracking-tighter uppercase italic -mb-1">{reg.name}</h3>
                        <p className="text-xs text-slate-400 font-black tracking-widest">{reg.email}</p>
                        {reg.gender && (
                          <div className="mt-2 text-left">
                            <span className={cn(
                              "inline-block text-[9px] font-black uppercase tracking-widest px-2.5 py-1 rounded-lg border",
                              reg.gender === 'wanita' 
                                ? "bg-pink-50 text-pink-600 border-pink-100" 
                                : "bg-emerald-50 text-emerald-600 border-emerald-100"
                            )}>
                              {reg.gender === 'wanita' ? 'Wanita ♀' : 'Pria ♂'}
                            </span>
                          </div>
                        )}
                        <div className="flex flex-wrap gap-2 mt-4">
                          {reg.specialties?.map((s: string) => (
                            <span key={s} className="px-3 py-1 bg-emerald-50 text-emerald-600 text-[9px] font-black uppercase tracking-widest border border-emerald-100 rounded-lg">{s}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 italic">Pending Since</p>
                        <p className="text-xs font-black text-slate-900 uppercase">24.05.2023</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 bg-slate-50 p-6 rounded-2xl border border-slate-100 text-xs">
                    <div>
                      <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Pengalaman</p>
                      <p className="font-extrabold text-slate-800">{reg.experience} Tahun</p>
                    </div>
                    <div>
                      <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Kelengkapan Alat</p>
                      <span className={cn(
                        "inline-block font-extrabold text-[10px] px-2 py-0.5 rounded-md",
                        reg.toolsCompleteness === 'Lengkap' ? "bg-emerald-100 text-emerald-800" :
                        reg.toolsCompleteness === 'Belum lengkap' ? "bg-amber-100 text-amber-800" :
                        "bg-red-100 text-red-800"
                      )}>
                        {reg.toolsCompleteness || 'Tidak ada/Ditentukan'}
                      </span>
                    </div>
                    <div>
                      <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">No. WhatsApp</p>
                      <p className="font-extrabold text-slate-800">{reg.phone || '-'}</p>
                    </div>
                    <div className="col-span-full border-t border-slate-100 pt-3">
                      <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Alamat/Lokasi</p>
                      <p className="font-semibold text-slate-700">{reg.district}, {reg.city}, {reg.province}</p>
                    </div>
                  </div>

                  <div className="space-y-4 bg-slate-50 p-6 rounded-2xl border border-slate-100">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Barber Bio</p>
                    <p className="text-xs text-slate-600 font-medium leading-relaxed italic">"{reg.bio || 'Tidak ada deskripsi biografi disediakan.'}"</p>
                  </div>

                  <div className="flex gap-4">
                    <button 
                      onClick={() => onRejectBarber(reg.id)}
                      className="flex-1 py-5 bg-white text-red-500 border-2 border-red-500 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] hover:bg-red-50 transition-all shadow-lg active:scale-95"
                    >
                      TERMINATE
                    </button>
                    <button 
                      onClick={() => onApproveBarber(reg)}
                      className="flex-[2] py-5 bg-emerald-500 text-slate-900 border-2 border-slate-900 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] shadow-xl hover:shadow-[6px_6px_0px_0px_rgba(15,23,42,1)] transition-all active:scale-95"
                    >
                      AUTHORIZE ACCESS
                    </button>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        )}

        {/* User Management View */}
        {activeTab === 'users' && (
          <div className="space-y-12">
            <div className="flex flex-col md:flex-row gap-6 items-center justify-between">
              <div className="relative w-full max-w-2xl group">
                <Search className="absolute left-8 top-1/2 -translate-y-1/2 text-slate-300 w-5 h-5 group-focus-within:text-emerald-500 transition-colors" />
                <input 
                  type="text" 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="SEARCH DIRECTORY (NAME, EMAIL, UID...)"
                  className="w-full bg-white border-2 border-slate-900 rounded-[2rem] pl-16 pr-8 py-6 text-xs font-black uppercase tracking-widest focus:ring-4 focus:ring-emerald-500/10 outline-none transition-all shadow-xl placeholder:text-slate-200"
                />
              </div>
              <div className="flex gap-4">
                <button className="flex items-center gap-3 px-10 py-6 bg-slate-900 text-white rounded-[2rem] text-[10px] font-black uppercase tracking-widest hover:translate-y-1 transition-all shadow-[6px_6px_0px_0px_#10B981]">
                  <Filter className="w-5 h-5" />
                  Sort Params
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredUsers.map((u, i) => (
                <motion.div 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.02 }}
                  key={u.uid}
                  className="bg-white p-10 border-2 border-slate-900 rounded-[2.5rem] group relative hover:shadow-[12px_12px_0px_0px_rgba(16,185,129,0.1)] transition-all"
                >
                  <div className="flex flex-col items-center text-center mb-10">
                    <div className="relative mb-6">
                      <img src={u.avatar} className="w-24 h-24 rounded-[2rem] object-cover border-4 border-white shadow-2xl grayscale group-hover:grayscale-0 transition-all" alt="" />
                      <div className={cn(
                        "absolute -top-3 -right-3 w-10 h-10 rounded-2xl border-4 border-white flex items-center justify-center shadow-lg",
                        u.role === 'admin' ? "bg-red-500" : u.role === 'barber' ? "bg-emerald-500" : "bg-blue-500"
                      )}>
                        {u.role === 'admin' ? <ShieldCheck className="w-5 h-5 text-white" /> : <UserCircleIcon className="w-5 h-5 text-white" />}
                      </div>
                    </div>
                    <h3 className="text-2xl font-black text-slate-900 tracking-tighter uppercase italic leading-tight mb-2 group-hover:text-emerald-600 transition-colors">{u.name}</h3>
                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-[0.2em]">{u.email}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-10">
                    <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 flex flex-col items-center justify-center">
                      <p className="text-[8px] text-slate-400 font-black uppercase tracking-widest mb-1">Rank</p>
                      <p className="text-[10px] font-black text-slate-900 uppercase">{u.role}</p>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 flex flex-col items-center justify-center">
                      <p className="text-[8px] text-slate-400 font-black uppercase tracking-widest mb-1">Zone</p>
                      <p className="text-[10px] font-black text-slate-900 uppercase truncate w-full text-center">{u.city || 'N/A'}</p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <select 
                      value={u.role}
                      onChange={(e) => onUpdateUserRole(u.uid, e.target.value)}
                      className="flex-1 bg-slate-900 text-white border-none rounded-2xl px-6 py-4 text-[10px] font-black uppercase tracking-widest outline-none transition-all cursor-pointer hover:bg-slate-800"
                    >
                      <option value="customer">Role: User</option>
                      <option value="barber">Role: Barber</option>
                      <option value="admin">Role: Root</option>
                    </select>
                    <button 
                      onClick={() => onDeleteUser(u.uid)}
                      className="w-16 h-14 bg-white text-red-500 hover:bg-red-500 hover:text-white rounded-2xl flex items-center justify-center transition-all border-2 border-red-500 active:scale-95 shadow-[4px_4px_0px_0px_#DE3341] hover:shadow-none"
                      title="Delete User"
                    >
                      <Trash2 className="w-6 h-6" />
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'bookings' && (
          <div className="space-y-12">
            <div className="flex items-center justify-between border-b-4 border-slate-900 pb-6">
              <div className="flex items-center gap-4">
                <div className="w-4 h-4 bg-emerald-500 border-2 border-slate-900 rotate-45" />
                <h3 className="text-3xl font-black text-slate-900 uppercase tracking-tighter italic">Ledger Log</h3>
              </div>
            </div>
                 <div className="grid grid-cols-1 gap-[1px] bg-slate-200 border-2 border-slate-900 overflow-hidden shadow-[12px_12px_0px_0px_rgba(241,245,249,1)]">
              {bookings.map((b) => (
                <div key={b.id} className="bg-white p-8 md:p-10 hover:bg-slate-50 transition-all flex flex-col gap-6 group border-b border-slate-100 last:border-b-0">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-8">
                    <div className="flex items-center gap-8">
                      <div className="hidden sm:flex flex-col items-center justify-center w-16 h-16 border-2 border-slate-100 italic font-black text-slate-200 uppercase text-[10px] bg-slate-50 group-hover:bg-emerald-500 group-hover:text-white group-hover:border-slate-900 transition-all">
                        {b.date.split(' ')[0].slice(0,3)}
                      </div>
                      <div>
                        <div className="flex items-center gap-3 mb-1">
                          <p className="font-black text-xl text-slate-900 uppercase tracking-tight">{b.customerName}</p>
                          <span className="text-[9px] font-black bg-slate-900 text-white px-2 py-0.5 rounded-sm">#{b.id.slice(-6).toUpperCase()}</span>
                        </div>
                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-[0.1em]">Layanan: {b.serviceName} • {b.time}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-8 justify-between md:justify-end">
                      <div className="text-right hidden sm:block">
                        <p className="text-[9px] text-slate-400 font-black uppercase tracking-widest mb-1">Kapster</p>
                        <p className="text-xs font-black text-slate-900">{b.barberName}</p>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <div className="flex items-center gap-4">
                          <p className="text-lg font-black text-slate-900 tabular-nums leading-none">Rp {b.totalPrice.toLocaleString()}</p>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              onDeleteBooking(b.id);
                            }}
                            className="p-3 text-red-400 hover:text-white transition-all rounded-xl hover:bg-red-500 border border-transparent hover:border-red-600 shadow-sm"
                            title="Delete Transaction"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <StatusBadge status={b.status} />
                      </div>
                    </div>
                  </div>

                  {/* Admin panel payment sub row */}
                  <div className="pt-4 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <span className="text-[10px] font-black uppercase text-slate-400 tracking-wider">Pembayaran:</span>
                      <span className={cn(
                        "px-2.5 py-1 rounded text-[9px] font-black uppercase tracking-widest border",
                        b.paymentStatus === 'Lunas' ? "bg-emerald-50 text-emerald-600 border-emerald-100 animate-none" :
                        b.paymentStatus === 'Menunggu Verifikasi' ? "bg-amber-50 text-amber-600 border-amber-100 animate-pulse font-extrabold" :
                        "bg-rose-50 text-rose-600 border-rose-100"
                      )}>
                        {b.paymentStatus || 'Belum Bayar'}
                      </span>
                      <span className="text-xs font-bold text-slate-600">
                        via {b.paymentMethod || 'QRIS'}
                      </span>
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                      {b.paymentProofUrl && (
                        <button
                          onClick={() => {
                            const w = window.open();
                            if (w) w.document.write(`<img src="${b.paymentProofUrl}" style="max-width: 100%; border-radius: 12px; box-shadow: 0 4px 12px rgba(0,0,0,0.15);" />`);
                          }}
                          className="px-3 py-1.5 bg-slate-50 hover:bg-slate-100 hover:text-slate-900 text-slate-600 rounded-lg text-[10px] font-black uppercase tracking-wider cursor-pointer border border-slate-200 transition-colors"
                        >
                          Lihat Bukti
                        </button>
                      )}
                      
                      {b.paymentStatus !== 'Lunas' && (
                        <button
                          onClick={async () => {
                            try {
                              await dbService.verifyPayment(b.id, 'Lunas');
                            } catch (err) {
                              console.error('Failed to verify payment', err);
                            }
                          }}
                          className="px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-[10px] font-black uppercase tracking-wider cursor-pointer transition-colors shadow-lg shadow-emerald-500/10"
                        >
                          Verifikasi Lunas
                        </button>
                      )}
                      
                      {b.paymentStatus === 'Menunggu Verifikasi' && (
                        <button
                          onClick={async () => {
                            try {
                              await dbService.verifyPayment(b.id, 'Belum Bayar');
                            } catch (err) {
                              console.error('Failed to reject payment', err);
                            }
                          }}
                          className="px-3 py-1.5 bg-rose-500 hover:bg-rose-600 text-white rounded-lg text-[10px] font-black uppercase tracking-wider cursor-pointer transition-colors shadow-sm"
                        >
                          Tolak Bukti
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Global Settings with Brutalist Forms */}
        {activeTab === 'settings' && (
          <div className="max-w-5xl space-y-16">
            <section>
              <div className="flex items-center gap-6 mb-12">
                <h3 className="text-4xl font-black text-slate-900 tracking-tighter uppercase italic">Platform Identity</h3>
                <div className="h-[2px] flex-1 bg-slate-900" />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                <div className="bg-white border-2 border-slate-900 rounded-[2rem] p-10 shadow-[10px_10px_0px_0px_#10B981] space-y-8">
                  <div className="flex items-center justify-between">
                    <div className="max-w-xs">
                      <p className="text-xl font-black text-slate-900 italic uppercase mb-2">Auto-Barbering</p>
                      <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest leading-relaxed">Allow instant verification for new barber applications</p>
                    </div>
                    <button className="w-16 h-8 bg-slate-900 rounded-full p-1 transition-all">
                      <div className="w-6 h-6 bg-emerald-500 rounded-full translate-x-8" />
                    </button>
                  </div>
                  <div className="pt-8 border-t border-slate-50 flex items-center justify-between">
                    <p className="text-xl font-black text-slate-900 italic uppercase">System Fee</p>
                    <div className="flex items-center gap-4">
                       <input type="number" defaultValue="10" className="w-20 bg-slate-50 border-2 border-slate-900 rounded-xl px-4 py-3 text-sm font-black outline-none focus:bg-white" />
                       <span className="text-xs font-black text-slate-900">%</span>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-900 border-2 border-slate-900 rounded-[3rem] p-12 text-white flex flex-col justify-between overflow-hidden relative group">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500 rotate-45 translate-x-16 -translate-y-16 group-hover:scale-110 transition-transform" />
                  <h4 className="text-[10px] font-black uppercase tracking-[0.5em] text-emerald-400 mb-8">Maintenance State</h4>
                  <div className="space-y-6">
                    <button className="w-full py-6 bg-white text-slate-900 rounded-2xl text-[11px] font-black uppercase tracking-[0.3em] shadow-xl hover:translate-x-1 transition-all">
                      Toggle Build Mode
                    </button>
                    <p className="text-[9px] text-slate-500 font-bold uppercase text-center px-10">Activating build mode will disable access for non-root users</p>
                  </div>
                </div>
              </div>
            </section>

            <section className="bg-red-50 border-2 border-red-200 rounded-[3rem] p-12 lg:p-20 relative overflow-hidden">
              <XCircle className="absolute -bottom-10 -right-10 w-64 h-64 text-red-100 rotate-12" />
              <div className="relative">
                <div className="flex items-center gap-4 text-red-500 mb-10">
                  <AlertCircle className="w-10 h-10" />
                  <h3 className="text-4xl font-black tracking-tighter uppercase italic">DESTRUCTION ZONE</h3>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <motion.button 
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={onClearUsers}
                    className="flex flex-col items-start gap-4 p-10 bg-white border-2 border-red-500 rounded-3xl text-left group hover:bg-red-500 transition-all"
                  >
                    <Users className="w-10 h-10 text-red-500 group-hover:text-white" />
                    <div>
                      <p className="text-2xl font-black text-slate-900 group-hover:text-white italic uppercase">ERASE DIRECTORY</p>
                      <p className="text-[10px] text-red-400 group-hover:text-white/80 font-bold uppercase tracking-widest mt-2 leading-relaxed">Permanently purge all user data profiles and authentication records.</p>
                    </div>
                  </motion.button>

                  <motion.button 
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="flex flex-col items-start gap-4 p-10 bg-white border-2 border-slate-900 rounded-3xl text-left group hover:bg-slate-900 transition-all"
                  >
                    <Calendar className="w-10 h-10 text-slate-900 group-hover:text-emerald-400" />
                    <div>
                      <p className="text-2xl font-black text-slate-900 group-hover:text-white italic uppercase">WIPE LEDGER</p>
                      <p className="text-[10px] text-slate-400 group-hover:text-white/80 font-bold uppercase tracking-widest mt-2 leading-relaxed">Reset all booking history, revenue logs and transaction data points.</p>
                    </div>
                  </motion.button>
                </div>
              </div>
            </section>
          </div>
        )}
      </main>
    </div>
  );
};


const UserCircleIcon = ({ className }: { className?: string }) => (
    <svg 
      className={className} 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2.5" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    >
      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
);
